import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Compass, Briefcase, DollarSign, Building2, Home, Heart, Brain,
  Sparkles, ChevronDown, ChevronRight, ExternalLink, CheckCircle2,
  BookOpen, Users, ShieldCheck, ArrowRight
} from 'lucide-react';
import AppLogo from '../components/AppLogo';
import CareProfileResourceCard from '../components/assessments/CareProfileResourceCard';

const ExtLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-800 dark:text-cyan-400 dark:hover:text-cyan-300 transition-colors underline-offset-2 hover:underline">
    {children} <ExternalLink className="w-3 h-3 flex-shrink-0" />
  </a>
);

const Section = ({ icon: Icon, color, title, subtitle, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="surface-card rounded-2xl overflow-hidden" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-6 py-5 flex items-center gap-4 text-left hover:bg-muted/30 transition-colors">
        <div className={`w-11 h-11 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
      </button>
      {open && <div className="px-6 pb-6 space-y-5 border-t border-border/50">{children}</div>}
    </section>
  );
};

const SubSection = ({ title, children }) => (
  <div className="space-y-2.5 pt-4">
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    {children}
  </div>
);

const ToolItem = ({ children }) => (
  <div className="flex items-start gap-2.5 text-sm text-foreground">
    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

const ResourceHub = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pb-24" data-testid="resource-hub-page">
      <header className="surface-header sticky top-0 z-40 border-b border-cyan-100">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center gap-3">
          <AppLogo />
          <div>
            <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Resource Hub</h1>
            <p className="text-xs text-muted-foreground">Help for the life happening around cancer</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        {/* Intro */}
        <div className="text-center space-y-4 py-4">
          <p className="text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
            When you're diagnosed with cancer (or supporting someone who is), the hardest part isn't just medical. It's figuring out:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['what to do next', 'who to call', 'what matters now', 'how to keep life from falling apart'].map(item => (
              <span key={item} className="text-sm font-medium text-foreground bg-cyan-50 dark:bg-cyan-900/20 px-3 py-1.5 rounded-full">{item}</span>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">This hub organizes trusted resources into clear action paths — so you don't have to.</p>
        </div>

        {/* Your Care Profile / Caregiver Style */}
        <CareProfileResourceCard />

        {/* Start Here */}
        <Section icon={Compass} color="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300" title="Start Here: What Do I Do Right Now?" subtitle="If you're overwhelmed, begin here." defaultOpen={true}>
          <SubSection title="First steps after diagnosis">
            <ToolItem>What to do after a diagnosis (checklist)</ToolItem>
            <ToolItem>Questions to ask your doctor at your next appointment</ToolItem>
            <ToolItem>What to tell your employer (simple script)</ToolItem>
            <ToolItem>Who to tell — and how</ToolItem>
          </SubSection>

          <SubSection title="Understanding Ring Theory">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Ring Theory is a model for providing support during crisis. Imagine concentric circles:
            </p>
            <div className="space-y-1.5 pl-2">
              <p className="text-sm text-foreground"><strong>Inner Circle (Center):</strong> The person experiencing illness, recovery, aging, or disability</p>
              <p className="text-sm text-foreground"><strong>Middle Circle:</strong> Close family, caregivers, and key helpers</p>
              <p className="text-sm text-foreground"><strong>Outer Circle:</strong> Friends, extended family, coworkers, and community</p>
            </div>
            <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-xl p-4 mt-3">
              <p className="text-sm font-semibold text-cyan-800 dark:text-cyan-200">Key Rule: Comfort IN, Dump OUT</p>
              <ul className="text-sm text-cyan-700 dark:text-cyan-300 mt-2 space-y-1">
                <li>Support flows inward toward the center</li>
                <li>Stress and venting should flow outward</li>
                <li>Each circle supports the people closer to the center</li>
                <li>Never complain to someone in a circle closer to the center than you</li>
              </ul>
            </div>
          </SubSection>

          <SubSection title="How to support without advice">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { title: 'Listen Actively', items: ['Give full attention', "Don't interrupt", 'Acknowledge their feelings'] },
                { title: 'Validate Emotions', items: ['"That sounds really hard"', '"I understand why you feel that way"', 'Avoid "at least" statements'] },
                { title: 'Offer Specific Help', items: ['"Can I bring dinner Tuesday?"', '"I\'m free to drive you"', 'Concrete actions, not vague offers'] },
                { title: 'Follow Their Lead', items: ['Ask what they need', 'Respect boundaries', "Don't assume you know best"] },
              ].map(card => (
                <div key={card.title} className="bg-muted/40 rounded-xl p-3 space-y-1.5">
                  <p className="text-sm font-semibold text-foreground">{card.title}</p>
                  {card.items.map(item => <p key={item} className="text-xs text-muted-foreground">- {item}</p>)}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground italic mt-2">Remember: Presence matters more than words.</p>
          </SubSection>

          <SubSection title="The CircleUpSquad Principle">
            <div className="bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-cyan-900/20 dark:to-emerald-900/20 rounded-xl p-4 text-center space-y-2">
              <blockquote className="text-sm text-foreground italic leading-relaxed">
                "No one should have to become the coordinator of their own survival alone."
              </blockquote>
              <p className="text-xs text-muted-foreground">
                This hub exists to reduce the invisible work of cancer — so you can focus on living your life, not managing its breakdown.
              </p>
            </div>
          </SubSection>

          <SubSection title="No Ghost Zone">
            <div className="bg-rose-50 dark:bg-rose-900/15 rounded-xl p-4 text-center space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Cancer can isolate people — not because they stop caring, but because no one knows how to stay connected.
              </p>
              <p className="text-sm font-semibold text-foreground">
                We help change that. CircleUpSquad is a No Ghost Zone.
              </p>
            </div>
          </SubSection>
        </Section>

        {/* Work */}
        <Section icon={Briefcase} color="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" title="Work, Job Protection & Income" subtitle="For working adults trying to stay employed or protect their job.">
          <SubSection title="Understand your rights">
            <ToolItem>Family and Medical Leave Act (FMLA)</ToolItem>
            <ToolItem>Americans with Disabilities Act (ADA accommodations)</ToolItem>
          </SubSection>
          <SubSection title="Helpful resources">
            <ExtLink href="https://www.dol.gov/agencies/whd/fmla">U.S. Department of Labor — FMLA</ExtLink>
            <ExtLink href="https://adata.org/">ADA National Network</ExtLink>
            <ExtLink href="https://askjan.org/">Job Accommodation Network (JAN)</ExtLink>
            <ExtLink href="https://www.cancerandcareers.org/">Cancer and Careers (workplace guidance)</ExtLink>
          </SubSection>
          <SubSection title="CircleUpSquad tools">
            <ToolItem>HR disclosure script generator</ToolItem>
            <ToolItem>"What do I tell my boss?" templates</ToolItem>
            <ToolItem>Return-to-work planning guide</ToolItem>
          </SubSection>
        </Section>

        {/* Money */}
        <Section icon={DollarSign} color="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" title="Money, Bills & Financial Support" subtitle="Help with the cost of cancer and daily life.">
          <SubSection title="Financial assistance programs">
            <ExtLink href="https://www.cancercare.org/financial">CancerCare financial assistance</ExtLink>
            <ExtLink href="https://www.patientadvocate.org/">Patient Advocate Foundation</ExtLink>
            <ExtLink href="https://www.needymeds.org/">NeedyMeds</ExtLink>
            <ExtLink href="https://www.cancer.org/support-programs-and-services.html">American Cancer Society financial help</ExtLink>
          </SubSection>
          <SubSection title="CircleUpSquad tools">
            <ToolItem>"Where can I get help paying for this?" checklist</ToolItem>
            <ToolItem>Script for talking to hospital social workers</ToolItem>
            <ToolItem>Copay assistance finder guide</ToolItem>
          </SubSection>
        </Section>

        {/* Insurance */}
        <Section icon={Building2} color="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" title="Insurance & Healthcare Navigation" subtitle="Understanding coverage, bills, and approvals.">
          <SubSection title="Trusted resources">
            <ExtLink href="https://www.kff.org/">Kaiser Family Foundation (KFF)</ExtLink>
            <ExtLink href="https://www.medicarerights.org/">Medicare Rights Center</ExtLink>
            <ExtLink href="https://www.shiphelp.org/">SHIP (State Health Insurance Assistance Programs)</ExtLink>
          </SubSection>
          <SubSection title="CircleUpSquad tools">
            <ToolItem>Insurance question checklist for appointments</ToolItem>
            <ToolItem>Prior authorization explainer</ToolItem>
            <ToolItem>"What does this bill mean?" guide</ToolItem>
          </SubSection>
        </Section>

        {/* Help at Home */}
        <Section icon={Home} color="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" title="Help at Home & Daily Life Support" subtitle="Coordinating meals, rides, childcare, and practical help.">
          <SubSection title="Trusted tools">
            <ExtLink href="https://www.caringbridge.org/">CaringBridge</ExtLink>
            <ExtLink href="https://www.mealtrain.com/">Meal Train</ExtLink>
            <ExtLink href="https://lotsahelpinghands.com/">Lotsa Helping Hands</ExtLink>
          </SubSection>
          <SubSection title="CircleUpSquad tools">
            <ToolItem>"How to ask for help" message templates</ToolItem>
            <ToolItem>Support circle setup guide</ToolItem>
            <ToolItem>Delegation planner (who does what, when)</ToolItem>
          </SubSection>

          <SubSection title="Self-care for caregivers">
            <p className="text-sm text-muted-foreground leading-relaxed">Taking care of yourself isn't selfish — it's essential. You can't pour from an empty cup.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div className="bg-amber-50 dark:bg-amber-900/15 rounded-xl p-3 space-y-1">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Daily Practices</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">Set boundaries on time and energy</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">Take regular breaks, even if short</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">Maintain your own health appointments</p>
              </div>
              <div className="bg-rose-50 dark:bg-rose-900/15 rounded-xl p-3 space-y-1">
                <p className="text-xs font-semibold text-rose-800 dark:text-rose-200">Warning Signs of Burnout</p>
                <p className="text-xs text-rose-700 dark:text-rose-300">Constant exhaustion</p>
                <p className="text-xs text-rose-700 dark:text-rose-300">Increased irritability</p>
                <p className="text-xs text-rose-700 dark:text-rose-300">Feeling resentful or neglecting your needs</p>
              </div>
            </div>
          </SubSection>
        </Section>

        {/* Emotional Support */}
        <Section icon={Heart} color="bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" title="Emotional Support & Connection" subtitle="Because no one should go through this alone.">
          <SubSection title="Support networks">
            <ExtLink href="https://www.cancersupportcommunity.org/">Cancer Support Community</ExtLink>
            <ExtLink href="https://imermanangels.org/">Imerman Angels (peer mentors)</ExtLink>
            <ExtLink href="https://www.cancercare.org/">CancerCare support groups</ExtLink>
          </SubSection>
          <SubSection title="CircleUpSquad tools">
            <ToolItem>"Who is in your circle?" mapping tool</ToolItem>
            <ToolItem>Caregiver support guidance</ToolItem>
            <ToolItem>"No Ghost Zone" connection practices</ToolItem>
          </SubSection>

          <SubSection title="Managing difficult emotions">
            <p className="text-sm text-muted-foreground leading-relaxed">Crisis brings up complex feelings. All emotions are valid — fear, anger, guilt, grief, hope, gratitude.</p>
            <div className="bg-rose-50 dark:bg-rose-900/15 rounded-xl p-4 space-y-2 mt-2">
              <p className="text-xs font-semibold text-rose-800 dark:text-rose-200">Healthy processing</p>
              <p className="text-xs text-rose-700 dark:text-rose-300">1. Acknowledge feelings without judgment</p>
              <p className="text-xs text-rose-700 dark:text-rose-300">2. Share with appropriate circles (remember: dump OUT)</p>
              <p className="text-xs text-rose-700 dark:text-rose-300">3. Physical activity helps release stress</p>
              <p className="text-xs text-rose-700 dark:text-rose-300">4. Journaling or reflection</p>
              <p className="text-xs text-rose-700 dark:text-rose-300">5. Professional support when needed</p>
            </div>
            <p className="text-xs text-muted-foreground italic mt-2">Emotions aren't problems to fix — they're information to honor.</p>
          </SubSection>
        </Section>

        {/* Medical Understanding */}
        <Section icon={Brain} color="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" title="Medical Understanding & Decision Support" subtitle="Making sense of treatment, options, and information.">
          <SubSection title="Trusted sources">
            <ExtLink href="https://www.cancer.org/">American Cancer Society</ExtLink>
            <ExtLink href="https://www.cancer.gov/">National Cancer Institute</ExtLink>
            <ExtLink href="https://www.cancer.net/">Cancer.Net (ASCO)</ExtLink>
          </SubSection>
          <SubSection title="CircleUpSquad tools">
            <ToolItem>Appointment prep question generator</ToolItem>
            <ToolItem>Treatment decision worksheet</ToolItem>
            <ToolItem>Medical note organizer</ToolItem>
          </SubSection>
        </Section>

        {/* Life After */}
        <Section icon={Sparkles} color="bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300" title="Life, Identity & Work After Cancer" subtitle="For what comes next — not just survival, but living.">
          <SubSection title="Resources">
            <ExtLink href="https://www.cancerandcareers.org/">Cancer and Careers</ExtLink>
            <ExtLink href="https://triagecancer.org/">Triage Cancer</ExtLink>
          </SubSection>
          <SubSection title="CircleUpSquad tools">
            <ToolItem>Return-to-work identity reflection guide</ToolItem>
            <ToolItem>Energy + capacity planning tool</ToolItem>
            <ToolItem>Life rebuilding roadmap</ToolItem>
          </SubSection>
        </Section>

        {/* Want to try coaching? */}
        <section className="mt-8">
          <button
            onClick={() => navigate('/coach')}
            className="w-full surface-card rounded-xl p-5 text-center hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors"
            data-testid="try-coaching-btn"
          >
            <p className="text-base font-semibold text-foreground">Want to try coaching?</p>
            <p className="text-xs text-muted-foreground mt-1">Meet Shannon — first session is free</p>
          </button>
        </section>
      </div>
    </div>
  );
};

export default ResourceHub;
